import { CornerDownLeft } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Segmented } from '@/components/ui/segmented';
import { ASK, type AskConfig, type Verb } from '@/features/actions/verbs';
import { cn } from '@/lib/utils';

/**
 * One ask box open at a time, per component that owns one.
 *
 * `id` is whatever identifies the row: a brief, a task, a user or an invite. Two
 * verbs inside one row can never both be open, which is the point -- a row with
 * two reason fields in it is a row nobody can read.
 */
export interface AskState {
  verb: Verb;
  id: string;
  seed?: string;
  /**
   * Replaces `ASK[verb].question` for this one instance. `markMerged` uses it
   * to name the dependency a step waits on (issue #24): the static copy in
   * `verbs.ts` cannot say which PR that is, since that is a fact about the
   * task the button was drawn for rather than about the verb.
   */
  question?: string;
}

export function useAsk() {
  const [ask, setAsk] = useState<AskState | null>(null);
  return {
    ask,
    open: (verb: Verb, id: string, seed?: string, question?: string) =>
      setAsk({ verb, id, seed, question }),
    close: () => setAsk(null),
    isOpen: (verb: Verb, id: string) => ask?.verb === verb && ask.id === id,
    openOn: (id: string) => (ask?.id === id ? ask : null),
  };
}

/**
 * The reason field, in place.
 *
 * No dialog: the question opens where the verb was pressed, so the row it is
 * about is still on screen while you answer. Enter confirms because it is a real
 * form, Escape cancels, and the field is focused and selected on mount -- which
 * is what makes "send back with a note" one gesture instead of four. A click
 * anywhere outside the box cancels the same way Escape does, so walking away
 * from a question you did not mean to open closes it without a second
 * gesture; neither one asks first, because Cancel never did either -- the
 * only verbs here with a cost are the ones a person confirms.
 */
export function AskBox({
  ask,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  ask: AskState;
  busy: boolean;
  error: string | null;
  /** `choice` is set only for a verb with `choices`; it is the chosen value. */
  onConfirm: (value: string, choice?: string) => void;
  onCancel: () => void;
}) {
  const config: AskConfig = ASK[ask.verb];
  const [value, setValue] = useState(ask.seed ?? '');
  const [choice, setChoice] = useState(config.choices?.[0]?.value ?? '');
  const chosen = config.choices?.find((option) => option.value === choice);
  const field = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLFormElement>(null);

  useEffect(() => {
    field.current?.focus();
    field.current?.select();
  }, []);

  useEffect(() => {
    // A click that lands anywhere else means the same thing Escape does: put
    // the row back the way it was. `mousedown` rather than `click` so the
    // press that starts outside is what counts, not wherever the pointer ends
    // up if the click is dragged back in over the box.
    function outside(event: MouseEvent) {
      if (box.current && !box.current.contains(event.target as Node)) onCancel();
    }
    document.addEventListener('mousedown', outside);
    return () => document.removeEventListener('mousedown', outside);
  }, [onCancel]);

  // A verb whose reason is required cannot be confirmed empty: the next run is
  // briefed by this note, and an empty note briefs it with the last reviewer's.
  const missing = Boolean(config.required) && !value.trim();

  return (
    <form
      ref={box}
      className="bg-sunken border-t px-gutter py-2"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        // The sheet this row may be inside listens for Escape on the window.
        // Closing the question is what the key means here, and it means it
        // first.
        event.stopPropagation();
        onCancel();
      }}
      onSubmit={(event) => {
        event.preventDefault();
        if (missing) return;
        onConfirm(value.trim(), config.choices ? choice : undefined);
      }}
    >
      {config.choices ? (
        // The two ways of doing it, chosen before the confirm rather than as two
        // buttons: one confirm button per box is what keeps Enter unambiguous.
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <Segmented
            value={choice}
            options={config.choices.map((option) => ({
              value: option.value,
              label: option.label,
            }))}
            onChange={setChoice}
            label={config.question}
          />
          {chosen ? <span className="text-fg-3 text-2xs">{chosen.hint}</span> : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-fg-2 text-xs">{ask.question ?? config.question}</span>
        {config.field ? (
          <Input
            ref={field}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            type={config.numeric ? 'number' : 'text'}
            min={config.numeric ? 1 : undefined}
            max={config.numeric ? 16 : undefined}
            maxLength={config.numeric ? undefined : 500}
            required={config.required}
            placeholder={config.field}
            className={cn('min-w-[11rem] flex-1', config.numeric && 'max-w-[6rem] flex-none')}
          />
        ) : (
          // A verb that asks nothing still needs somewhere for focus and Enter.
          <input ref={field} className="sr-only" readOnly aria-label={config.question} />
        )}
        <Button
          type="submit"
          size="sm"
          disabled={busy || missing}
          variant={config.tone === 'danger' ? 'destructive' : 'gate'}
          className="max-sm:h-9"
        >
          {busy ? 'Working…' : config.confirm}
          <CornerDownLeft className="opacity-60" aria-hidden />
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel} className="max-sm:h-9">
          Cancel
        </Button>
      </div>
      {error ? (
        <p className="text-bad pt-1.5 text-xs" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}
